/**
 * File: invoiceSends.ts
 * Path: packages/config/src/invoices/invoiceSends.ts
 * Description: The send log: every time an invoice, credit note or
 * statement leaves the business (emailed, sent on WhatsApp or downloaded),
 * one record of when, how, to whom, by whom, what happened to it and the
 * PDF exactly as it went (kept in Cloud Storage, with its hash).
 *
 *   invoices/<id>/sends/<sendId>
 *   creditNotes/<id>/sends/<sendId>
 *   statementSends/<sendId>
 *
 * Only the server writes them (the invoiceSending callable and the Resend
 * webhook); staff with the sales permission read them. An email's status
 * follows Resend's webhook events; WhatsApp and downloads are "recorded"
 * because nothing reports back on them.
 */

import { formatContractDate, formatRand } from "../contracts/contractFormat";
import { BUSINESS_CONTACT } from "../voice/contact";

export const SEND_COLLECTIONS = {
  /** Under invoices/<id> and creditNotes/<id>. */
  sends: "sends",
  /** Top level: a statement belongs to a client, not one document. */
  statementSends: "statementSends",
  /** invoiceReminders/<invoiceNumber>_<kind>: a reminder Liam sent or dismissed. */
  reminders: "invoiceReminders",
  /** resendEmails/<resendId>: which sends one email belongs to, for the webhook. Server only. */
  resendEmails: "resendEmails",
} as const;

/** Cloud Storage folder for the PDFs exactly as sent: sends/<document>/<documentId>/<sendId>.pdf */
export const SEND_STORAGE_PREFIX = "sends";

export const SEND_DOCUMENTS = ["invoice", "creditNote", "statement"] as const;
export type SendDocument = (typeof SEND_DOCUMENTS)[number];

export const SEND_CHANNELS = ["email", "whatsapp", "download"] as const;
export type SendChannel = (typeof SEND_CHANNELS)[number];

export const SEND_CHANNEL_LABELS: Record<SendChannel, string> = {
  email: "Email",
  whatsapp: "WhatsApp",
  download: "Download",
};

/**
 * Where a send stands. "sending": handed to Resend, no answer yet.
 * "recorded": WhatsApp or a download, which nothing reports back on. The
 * rest are Resend's own events (resend.com/docs/webhooks/event-types).
 */
export const SEND_STATUSES = [
  "sending",
  "recorded",
  "sent",
  "delayed",
  "delivered",
  "opened",
  "bounced",
  "complained",
  "suppressed",
  "failed",
] as const;
export type SendStatus = (typeof SEND_STATUSES)[number];

export const SEND_STATUS_LABELS: Record<SendStatus, string> = {
  sending: "Sending",
  recorded: "Recorded",
  sent: "Sent",
  delayed: "Delayed, still trying",
  delivered: "Delivered",
  opened: "Opened",
  bounced: "Bounced: not delivered",
  complained: "Marked as spam",
  suppressed: "Blocked by Resend",
  failed: "Failed",
};

/** True for a status that means the client did not get it. */
export const sendFailed = (status: SendStatus): boolean =>
  status === "bounced" ||
  status === "complained" ||
  status === "suppressed" ||
  status === "failed";

/** Resend's webhook event types and the status each one sets. Others (scheduled, received) are ignored. */
export const RESEND_EVENT_STATUS: Readonly<Record<string, SendStatus>> = {
  "email.sent": "sent",
  "email.delivery_delayed": "delayed",
  "email.delivered": "delivered",
  "email.opened": "opened",
  "email.clicked": "opened",
  "email.bounced": "bounced",
  "email.complained": "complained",
  "email.suppressed": "suppressed",
  "email.failed": "failed",
};

/**
 * How far along each status is. Webhooks can arrive out of order, so a
 * status only replaces one of a lower rank: "delivered" never goes back to
 * "sent", while a bounce or a spam complaint always shows.
 */
const STATUS_RANK: Record<SendStatus, number> = {
  sending: 0,
  recorded: 0,
  sent: 1,
  delayed: 2,
  delivered: 3,
  opened: 4,
  bounced: 5,
  complained: 5,
  suppressed: 5,
  failed: 5,
};

/** The status a send has after `next` arrives. */
export const nextSendStatus = (
  current: SendStatus,
  next: SendStatus,
): SendStatus => (STATUS_RANK[next] >= STATUS_RANK[current] ? next : current);

export const REMINDER_KINDS = [
  "beforeDue",
  "late3",
  "late14",
  "late30call",
] as const;
export type ReminderKind = (typeof REMINDER_KINDS)[number];

/** Why it was sent: the document itself, or one of the reminders. */
export type SendPurpose = "document" | ReminderKind;

/** One Resend event as it arrived, kept on the send. */
export interface SendEvent {
  type: string;
  /** Epoch ms Resend says it happened. */
  at: number;
  /** The webhook message id (svix-id), so a retried webhook is counted once. */
  webhookId: string;
}

/** How many Resend events a send keeps. */
export const SEND_EVENTS_KEPT = 20;

export interface DocumentSend {
  id: string;
  document: SendDocument;
  /** invoices/<id>, creditNotes/<id>; for a statement, the client id or "". */
  documentId: string;
  /** "INV-000012", "CN-000001" or "Statement". */
  number: string;
  channel: SendChannel;
  purpose: SendPurpose;
  /** The email address or phone number; "" for a download. */
  recipient: string;
  subject: string;
  /** The email's words or the WhatsApp message, as sent. */
  message: string;
  /** Epoch ms. */
  sentAt: number | null;
  sentBy: string;
  sentByEmail: string;
  status: SendStatus;
  statusAt: number | null;
  events: SendEvent[];
  /** Resend's email id. */
  resendId: string | null;
  /** Why it failed, in plain words. */
  error: string;
  /** The PDF exactly as sent (Cloud Storage), its SHA-256 and size; null when none went (a text-only reminder). */
  pdfPath: string | null;
  pdfSha256: string;
  pdfBytes: number;
  filename: string;
  /** For a statement: who and which dates. */
  statement: {
    clientId: string | null;
    party: string;
    from: string;
    to: string;
  } | null;
}

const text = (v: unknown, max: number): string =>
  typeof v === "string" ? v.slice(0, max) : "";
const millis = (v: unknown): number | null => {
  if (typeof v === "number") return v;
  const m = (v as { toMillis?: () => number } | null)?.toMillis;
  return typeof m === "function" ? m.call(v) : null;
};
const oneOf = <T extends string>(
  list: readonly T[],
  v: unknown,
  fallback: T,
): T => list.find((x) => x === v) ?? fallback;

/** Reads a stored send defensively; null when it isn't one. */
export const toDocumentSend = (
  id: string,
  data: unknown,
): DocumentSend | null => {
  const d = (data && typeof data === "object" ? data : {}) as Record<
    string,
    unknown
  >;
  const document = SEND_DOCUMENTS.find((x) => x === d.document);
  const channel = SEND_CHANNELS.find((x) => x === d.channel);
  if (!document || !channel) return null;
  const purpose: SendPurpose =
    d.purpose === "document"
      ? "document"
      : oneOf<SendPurpose>(REMINDER_KINDS, d.purpose, "document");
  const events = Array.isArray(d.events)
    ? d.events.flatMap((e) => {
        const ev = (e && typeof e === "object" ? e : {}) as Record<
          string,
          unknown
        >;
        return typeof ev.type === "string" && typeof ev.at === "number"
          ? [{ type: ev.type, at: ev.at, webhookId: text(ev.webhookId, 100) }]
          : [];
      })
    : [];
  const st = (
    d.statement && typeof d.statement === "object" ? d.statement : null
  ) as Record<string, unknown> | null;
  return {
    id,
    document,
    documentId: text(d.documentId, 128),
    number: text(d.number, 20),
    channel,
    purpose,
    recipient: text(d.recipient, 300),
    subject: text(d.subject, 300),
    message: text(d.message, 5000),
    sentAt: millis(d.sentAt),
    sentBy: text(d.sentBy, 128),
    sentByEmail: text(d.sentByEmail, 300),
    status: oneOf(SEND_STATUSES, d.status, "recorded"),
    statusAt: millis(d.statusAt),
    events,
    resendId: typeof d.resendId === "string" && d.resendId ? d.resendId : null,
    error: text(d.error, 500),
    pdfPath: typeof d.pdfPath === "string" && d.pdfPath ? d.pdfPath : null,
    pdfSha256: text(d.pdfSha256, 64),
    pdfBytes: typeof d.pdfBytes === "number" ? d.pdfBytes : 0,
    filename: text(d.filename, 200),
    statement: st
      ? {
          clientId:
            typeof st.clientId === "string" && st.clientId ? st.clientId : null,
          party: text(st.party, 200),
          from: text(st.from, 10),
          to: text(st.to, 10),
        }
      : null,
  };
};

/** Newest first. */
export const sortSends = (sends: readonly DocumentSend[]): DocumentSend[] =>
  [...sends].sort(
    (a, b) => (b.sentAt ?? 0) - (a.sentAt ?? 0) || b.id.localeCompare(a.id),
  );

/** The first time it left, counting only sends that didn't fail; null when it never has. */
export const firstSentAt = (sends: readonly DocumentSend[]): number | null =>
  sends
    .filter(
      (s) =>
        s.status !== "sending" && !sendFailed(s.status) && s.sentAt !== null,
    )
    .reduce<
      number | null
    >((min, s) => (min === null || (s.sentAt as number) < min ? (s.sentAt as number) : min), null);

/** Where a sent PDF is kept. */
export const sendPdfPath = (
  document: SendDocument,
  documentId: string,
  sendId: string,
): string =>
  document === "statement"
    ? `${SEND_STORAGE_PREFIX}/statements/${sendId}.pdf`
    : `${SEND_STORAGE_PREFIX}/${document === "invoice" ? "invoices" : "creditNotes"}/${documentId}/${sendId}.pdf`;

/** What the email or WhatsApp is about, for the ready-made words. */
export interface SendFacts {
  document: SendDocument;
  /** "INV-000012", "CN-000001"; "" for a statement. */
  number: string;
  /** The person's name, when known ("Joe Mokoena"). */
  contactName: string;
  party: string;
  /** Invoice: its total. Credit note: its amount. Statement: the closing balance. */
  cents: number;
  /** Invoice: what is still to pay, when part of it has been paid or credited. Left out: the whole total. */
  balanceCents?: number;
  /** Invoice: due date. Statement: the last day. Credit note: its date. */
  date: string;
  /** Invoice: what to pay with. Credit note: the invoice it credits. */
  reference: string;
  /** Statement: the first day. */
  from: string;
  sender: string;
}

const hello = (contactName: string) =>
  `Hi ${contactName.trim().split(/\s+/)[0] || "there"}`;

/** An invoice part paid or credited: the words name what is still to pay, not the whole total. */
const partly = (f: SendFacts): boolean =>
  f.balanceCents !== undefined &&
  f.balanceCents > 0 &&
  f.balanceCents !== f.cents;

const statementBalance = (cents: number) =>
  cents > 0
    ? `The balance due is ${formatRand(cents / 100)}.`
    : cents < 0
      ? `There is ${formatRand(-cents / 100)} in your favour.`
      : "Nothing is due.";

/** The standard email for a document sent from the admin; Liam may change the words before it goes. */
export const documentEmailWording = (
  f: SendFacts,
): { subject: string; body: string } => {
  const amount = formatRand(f.cents / 100);
  const sign = `Kind regards\n${f.sender}\n${BUSINESS_CONTACT.phone}`;
  switch (f.document) {
    case "invoice":
      return {
        subject: `Invoice ${f.number} from IDK Solutions`,
        body: [
          `${hello(f.contactName)},`,
          partly(f)
            ? `Please find invoice ${f.number} attached. It was for ${amount}, and ${formatRand((f.balanceCents ?? 0) / 100)} of it is still to pay. The due date is ${formatContractDate(f.date)}.`
            : `Please find invoice ${f.number} for ${amount} attached. It is due on ${formatContractDate(f.date)}.`,
          `Please pay with reference ${f.reference || f.number} so your payment is matched to it. Thank you.`,
          sign,
        ].join("\n\n"),
      };
    case "creditNote":
      return {
        subject: `Credit note ${f.number} from IDK Solutions`,
        body: [
          `${hello(f.contactName)},`,
          `Please find credit note ${f.number} for ${amount} on invoice ${f.reference} attached.`,
          sign,
        ].join("\n\n"),
      };
    case "statement":
      return {
        subject: `Your statement from IDK Solutions, ${formatContractDate(f.from)} to ${formatContractDate(f.date)}`,
        body: [
          `${hello(f.contactName)},`,
          `Please find your statement for ${formatContractDate(f.from)} to ${formatContractDate(f.date)} attached. ${statementBalance(f.cents)}`,
          "If anything looks wrong, reply to this email and we will sort it out.",
          sign,
        ].join("\n\n"),
      };
  }
};

/** The standard WhatsApp message to go with a document (Liam attaches the PDF himself). */
export const documentWhatsappText = (f: SendFacts): string => {
  const amount = formatRand(f.cents / 100);
  switch (f.document) {
    case "invoice":
      return partly(f)
        ? `${hello(f.contactName)}, here is invoice ${f.number}. ${formatRand((f.balanceCents ?? 0) / 100)} of the ${amount} is still to pay (due date ${formatContractDate(f.date)}). Please pay with reference ${f.reference || f.number}. Thank you! ${f.sender}`
        : `${hello(f.contactName)}, here is invoice ${f.number} for ${amount}, due on ${formatContractDate(f.date)}. Please pay with reference ${f.reference || f.number}. Thank you! ${f.sender}`;
    case "creditNote":
      return `${hello(f.contactName)}, here is credit note ${f.number} for ${amount} on invoice ${f.reference}. ${f.sender}`;
    case "statement":
      return `${hello(f.contactName)}, here is your statement for ${formatContractDate(f.from)} to ${formatContractDate(f.date)}. ${statementBalance(f.cents)} ${f.sender}`;
  }
};
